import React, { useState } from 'react';
import { Table } from 'antd';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import { issueReasonKey } from '../helpers/upload.utils';
import type { SheetIssue } from '../helpers/upload.types';
import styles from './issues-table.module.scss';

const EM_DASH = '—';

const formatIssueDate = (date: string) => {
  const parsed = DateTime.fromISO(date);
  return parsed.isValid ? parsed.toFormat('M/d/yyyy') : EM_DASH;
};

type IssuesTableProps = {
  issues: SheetIssue[];
  showSheetColumn?: boolean;
  maxRows?: number;
};

export const IssuesTable = ({ issues, showSheetColumn = false, maxRows }: IssuesTableProps) => {
  const lang = useSelector(getLang('SETTINGS'));
  const [isExpanded, setIsExpanded] = useState(false);

  const isTruncatable = maxRows !== undefined && issues.length > maxRows;
  const visibleIssues = isTruncatable && !isExpanded ? issues.slice(0, maxRows) : issues;

  const columns = [
    ...(showSheetColumn
      ? [{ title: lang.SHIFTS_COL_SHEET, dataIndex: 'sheetName', key: 'sheetName', width: '22%' }]
      : []),
    {
      title: lang.SHIFTS_COL_SHIFT,
      dataIndex: 'shiftTitle',
      key: 'shiftTitle',
      width: '22%',
      render: (shiftTitle: string) => <span className={styles.shiftTitle}>{shiftTitle}</span>,
    },
    {
      title: lang.SHIFTS_COL_DATE,
      dataIndex: 'date',
      key: 'date',
      width: '18%',
      render: (date: string) => <span className={styles.date}>{formatIssueDate(date)}</span>,
    },
    {
      title: lang.SHIFTS_COL_REASON,
      key: 'reason',
      render: (_: unknown, issue: SheetIssue) => (
        <span className={styles.reason}>
          {lang[issueReasonKey(issue.reason)].replace('{value}', issue.reasonContext?.value ?? '')}
        </span>
      ),
    },
  ];

  return (
    <div className={styles.root}>
      <Table
        columns={columns}
        dataSource={visibleIssues}
        rowKey={(issue: SheetIssue, index) => `${issue.sheetName}-${issue.shiftTitle}-${issue.date}-${index}`}
        pagination={false}
        size="small"
        rootClassName={styles.table}
      />
      {isTruncatable && (
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setIsExpanded(current => !current)}>
          {isExpanded
            ? lang.SHIFTS_SHOW_FEWER
            : lang.SHIFTS_SHOW_ALL_ROWS.replace('{count}', formatNumber(issues.length))}
        </button>
      )}
    </div>
  );
};
